import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

// Marquee'd word-set name strip pinned to the top of the canvas during guess mode.
// During an active round / intermission shows the round's word-set; otherwise
// shows all configured sets joined with " · ".
function wsBaseName(s) {
    const i = s.lastIndexOf('/');
    return i >= 0 ? s.slice(i + 1) : s;
}

function wsDisplayName(s, customName, t) {
    if (s === '__custom__') return ((customName || (t && t('frontPage.customWordsetDefaultName', 'Custom')) || 'Custom') + ' ' + ((t && t('frontPage.customWordsetSuffix', '(custom)')) || '(custom)'));
    return wsBaseName(s);
}

export default function WordsetMarquee({roomSettings, guessRound, guessIntermission}) {
    const {t} = useTranslation();
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [overflows, setOverflows] = useState(false);
    const [roundKey, setRoundKey] = useState(null);

    const sets = roomSettings && roomSettings.wordSets;
    const customName = roomSettings && roomSettings.customWordsName;
    const roundWordSet = (guessRound && guessRound.active && guessRound.wordSet);
    const text = roundWordSet
        ? wsBaseName(roundWordSet) // backend already formats with "(custom)"; strip any folder prefix
        : (sets || []).map(s => wsDisplayName(s, customName, t)).join('  ·  ');

    // Track round changes to trigger the pop-in animation
    const currentRoundNum = guessRound && guessRound.active && guessRound.roundNumber;
    useEffect(() => {
        if (currentRoundNum != null) setRoundKey(currentRoundNum);
    }, [currentRoundNum]);

    useEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) { setOverflows(false); return; }
        const check = () => setOverflows(measure.scrollWidth > container.clientWidth + 1);
        check();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(check);
        ro.observe(container);
        return () => ro.disconnect();
    }, [text]);

    if (!sets || sets.length === 0) return null;

    return (
        <div data-wordset="true" data-wordset-label={text} style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 99,
            pointerEvents: 'none', overflow: 'hidden',
            padding: '3px 8px', boxSizing: 'border-box',
            fontSize: 12, color: 'rgba(60,50,100,0.85)', fontFamily: 'monospace', fontWeight: 600,
            textAlign: 'center', whiteSpace: 'nowrap',
            animation: roundWordSet ? 'wordsetPopIn 2.25s ease-out' : undefined,
            animationIterationCount: 1,
            transformOrigin: 'center bottom',
        }} key={roundKey}>
            <span ref={containerRef} style={{
                display: 'inline-block', maxWidth: '100%', overflow: 'hidden',
                whiteSpace: 'nowrap', position: 'relative',
                ...(overflows ? {
                    WebkitMaskImage: 'linear-gradient(to right, transparent, #000 12px, #000 calc(100% - 12px), transparent)',
                    maskImage: 'linear-gradient(to right, transparent, #000 12px, #000 calc(100% - 12px), transparent)',
                } : {}),
            }}>
                <span ref={measureRef} style={{position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', pointerEvents: 'none'}}>{text}</span>
                {overflows ? (
                    <span style={{display: 'inline-flex', whiteSpace: 'nowrap', animation: 'wordsetMarquee 20s linear infinite'}}>
                        <span style={{flexShrink: 0}}>{text}&nbsp; · &nbsp;</span>
                        <span style={{flexShrink: 0}}>{text}&nbsp; · &nbsp;</span>
                    </span>
                ) : text}
            </span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Word choice overlay — shown to the drawer during the choosing phase.
export function WordChoiceOverlay({guessRound, currentUser, onWordChoice}) {
    const {t} = useTranslation();
    if (!(guessRound && guessRound.choosing && guessRound.wordChoices && guessRound.drawer === currentUser)) return null;
    return (
        <div className="word-choice-overlay">
            <div className="word-choice-timer-bar" key={guessRound.roundNumber}
                 style={{animationDuration: `${guessRound.choiceTime || 15}s`}} />
            <span className="word-choice-title">{t('drawing.yourTurnPick')}</span>
            <div className="word-choice-list">
                {guessRound.wordChoices.map((c, i) => (
                    <button key={i} className="word-choice-btn" onClick={() => onWordChoice(i)}>
                        {c.word}
                        {c.wordSet && <span className="word-choice-category">{c.wordSet}</span>}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Word reveal overlay — pinned to the bottom of the canvas during intermission.
export function WordRevealOverlay({guessIntermission, roomSettings, scale}) {
    if (!(guessIntermission && guessIntermission.active && guessIntermission.word)) return null;
    const ws = guessIntermission.wordSet;
    const showSet = ws && roomSettings && roomSettings.wordSets && roomSettings.wordSets.length > 1;
    const setLabel = ws ? (ws.lastIndexOf('/') >= 0 ? ws.slice(ws.lastIndexOf('/') + 1) : ws) : '';
    return (
        <div data-intermission="true" style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            zIndex: 101, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '12px 0', pointerEvents: 'none',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
        }}>
            <span style={{
                fontSize: Math.min(48, 48 * scale), fontWeight: 'bold', fontFamily: 'monospace',
                color: '#ffd666', textShadow: '0 2px 12px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.8)',
                WebkitTextStroke: '1.5px black',
                paintOrder: 'stroke fill',
                letterSpacing: 4, textTransform: 'uppercase',
            }}>
                {guessIntermission.word}
            </span>
            {showSet && (
                <span style={{
                    fontSize: Math.min(13, 13 * scale), color: '#aab', marginTop: 2,
                    textShadow: '0 1px 4px rgba(0,0,0,0.9)',
                }}>
                    {setLabel}
                </span>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vote panel — appears below the canvas during intermission for non-drawers.
export function VotePanel({guessIntermission, currentUser, isMobileLandscape, myVote, onVote, width}) {
    const {t} = useTranslation();
    if (!(guessIntermission && guessIntermission.active
        && guessIntermission.drawer
        && guessIntermission.drawer !== currentUser
        && !guessIntermission.isPeriodEnd)) return null;

    const positionStyle = isMobileLandscape
        ? {position: 'absolute', bottom: 0, zIndex: 20}
        : {marginBottom: -8, zIndex: 10, position: 'relative'};

    return (
        <div style={{
            width, textAlign: 'center',
            padding: isMobileLandscape ? '4px 0 2px' : '8px 0 4px',
            background: 'rgba(30,30,50,0.92)', borderRadius: '0 0 8px 8px',
            ...positionStyle,
            animation: 'voteSlideIn 0.4s ease-out',
        }}>
            <div style={{
                fontSize: 13, color: '#ccc', marginBottom: 4, fontFamily: 'monospace',
                animation: 'votePulse 1.5s ease-in-out infinite',
            }}>{t('drawing.rateDrawing')}</div>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12}}>
                <button onClick={() => onVote(false)}
                    style={{
                        width: 44, height: 44, fontSize: 24, border: 'none', borderRadius: 8, cursor: 'pointer',
                        background: myVote === false ? '#cc4444' : '#774444',
                        opacity: myVote != null && myVote !== false ? 0.35 : 1,
                        transition: 'all 0.15s',
                    }}>👎</button>
                <button onClick={() => onVote(true)}
                    style={{
                        width: 44, height: 44, fontSize: 24, border: 'none', borderRadius: 8, cursor: 'pointer',
                        background: myVote === true ? '#44aa44' : '#447744',
                        opacity: myVote != null && myVote !== true ? 0.35 : 1,
                        transition: 'all 0.15s',
                    }}>👍</button>
            </div>
            {myVote != null && <div style={{fontSize: 11, color: '#aaa', marginTop: 3}}>{t('drawing.voted')}</div>}
        </div>
    );
}
